import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ClientSideRowModelModule, ModuleRegistry, type ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { getMainAxios } from '../utils/util';
import { agGridBox, colors, emptyMsg, signColor, won, pct } from '../styles/fdsTheme';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

/**
 * 트레이딩 화면 하단 — 실제 계좌 데이터 탭 그리드 (2026-11-03)
 *
 *   주문     /api/v1/portfolio/orders   (부모가 읽어 넘긴다 — 주문 패널의 보유·매도가능 계산과 공유)
 *   슬라이스 /api/v1/pfbs/slices?pfId&d
 *   체결     /api/v1/pfbs/fills?pfId&startDate&endDate
 *   보유종목 /api/v1/portfolio/positions (부모) — KIS 평가손익 옆에 (현재가−평단)×수량 으로 **자체 계산**해 대조
 *   손익     /api/v1/pfbs/pnl (체결로 자체 계산한 일손익 vs KIS) + /portfolio/daily-detail 의 종목별 당일매매 손익
 *
 * 주문 행을 누르면 슬라이스·체결 탭이 그 주문으로 좁혀진다. 장중에는 10초마다 다시 읽는다(부모 refreshKey).
 */

type Tab = 'ord' | 'slice' | 'fill' | 'pos' | 'pnl';

interface Props {
  pfId: number | null;
  d: string;
  orders: any[];
  positions: any[];
  refreshKey: number;
  isFut: boolean;
  onPickProd: (prodId: string) => void;
  onCancel: (o: any) => void;
}

const arr = (d: any): any[] => (Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : []);
const numCol = (field: string, headerName: string, width = 90, extra: Partial<ColDef> = {}): ColDef => ({
  field, headerName, width, type: 'rightAligned', valueFormatter: p => (p.value == null || p.value === '' ? '' : won(p.value)), ...extra,
});
const signCell = { cellStyle: (p: any) => ({ color: signColor(p.value), textAlign: 'right' }) };
const isBuyOrd = (o: any) => (o.ls ?? 1) * (o.ex ?? 1) > 0;   // sgn = ls×ex
// OrderState: -9 거절 · 0 미접수 · 10 접수 · 20 작동 · 30 완료(취소 포함)
const ordState = (o: any) => (o.state === -9 || o.errMsg ? '거부'
  : o.state === 30 ? (o.cnclT ? (o.cumQty ? '부분체결·취소' : '취소') : o.cumQty ? '완료' : '미체결')
  : o.cnclT ? '취소중' : o.state === 20 ? '작동' : o.state === 10 ? '접수' : '미접수');
const cancellable = (o: any) => !o.errMsg && !o.cnclT && [0, 10, 20].includes(o.state);
const sideBadge = (buy: boolean) => (
  <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 11, fontWeight: 600,
    color: buy ? colors.up : colors.down, background: buy ? colors.buyBg : colors.sellBg }}>{buy ? '매수' : '매도'}</span>
);

const TradingAccountGrids: React.FC<Props> = ({ pfId, d, orders, positions, refreshKey, isFut, onPickProd, onCancel }) => {
  const axios = getMainAxios();
  const [tab, setTab] = useState<Tab>('ord');
  const [slices, setSlices] = useState<any[]>([]);
  const [fills, setFills] = useState<any[]>([]);
  const [pnl, setPnl] = useState<any[]>([]);
  const [tradePnl, setTradePnl] = useState<any[]>([]);
  const [selOrdId, setSelOrdId] = useState<number | null>(null);

  useEffect(() => {
    if (!pfId) return;
    axios.get('/api/v1/pfbs/slices', { params: { pfId, d } }).then(r => setSlices(arr(r.data))).catch(() => setSlices([]));
    axios.get('/api/v1/pfbs/fills', { params: { pfId, startDate: d, endDate: d } })
      .then(r => setFills(arr(r.data).sort((a: any, b: any) => String(a.ordT ?? '').localeCompare(String(b.ordT ?? '')))))
      .catch(() => setFills([]));
    axios.get('/api/v1/pfbs/pnl', { params: { pfId, startDate: d, endDate: d, limit: 20 } }).then(r => setPnl(arr(r.data))).catch(() => setPnl([]));
    axios.get('/api/v1/portfolio/daily-detail', { params: { id: pfId, d } }).then(r => setTradePnl(arr(r.data?.tradePnl))).catch(() => setTradePnl([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pfId, d, refreshKey]);
  useEffect(() => { setSelOrdId(null); }, [pfId, d]);

  const nmOf = useMemo(() => {
    const m = new Map<string, string>();
    orders.forEach(o => o.prodNm && m.set(o.prodId, o.prodNm));
    positions.forEach(p => p.prodNm && m.set(p.prodid ?? p.prodId, p.prodNm));
    return m;
  }, [orders, positions]);

  // 주문 선택 시 좁히기 — 체결은 그 주문 슬라이스의 KIS 주문번호로 잇는다
  const selSlices = useMemo(() => (selOrdId == null ? slices : slices.filter(s => s.ordId === selOrdId)), [slices, selOrdId]);
  const selFills = useMemo(() => {
    if (selOrdId == null) return fills;
    const nos = new Set(selSlices.map(s => String(s.ordNo ?? '')).filter(Boolean));
    return fills.filter(f => nos.has(String(f.ordNo)));
  }, [fills, selSlices, selOrdId]);

  const held = useMemo(() => positions.filter(p => p.pos).map(p => {
    // 선물은 승수(코스피200 25만·미니 5만)가 종목마다 달라 여기서 계산하지 않는다 — KIS 값만
    const calc = isFut ? null : ((p.c ?? 0) - (p.avgprc ?? 0)) * (p.pos ?? 0);
    return { ...p, prodId: p.prodid ?? p.prodId, calcPrft: calc, gap: calc == null ? null : calc - (p.prft ?? 0) };
  }), [positions, isFut]);

  // ── 컬럼 ──
  const ordCols = useMemo<ColDef[]>(() => [
    { field: 'id', headerName: 'ID', width: 76 },
    { field: 'hm0', headerName: '시각', width: 66 },
    { headerName: '종목', width: 160, valueGetter: p => p.data.prodNm ?? p.data.prodId, cellStyle: { fontWeight: 600 } },
    { headerName: '구분', width: 66, cellRenderer: (p: any) => sideBadge(isBuyOrd(p.data)) },
    { headerName: '가격', width: 90, type: 'rightAligned',
      valueGetter: p => (p.data.limitPrc ? won(p.data.limitPrc) : p.data.limitIdx != null ? `${p.data.limitIdx}틱` : '시장가') },
    numCol('qty2ord', '주문', 80, { valueGetter: p => p.data.qty2ord ?? p.data.qty2Ord }),
    numCol('cumQty', '체결', 80),
    numCol('avgPx', '평균가', 90),
    { headerName: '슬라이스', width: 80, type: 'rightAligned', valueGetter: p => (p.data.sliceTotal ? `${p.data.sliceDone ?? 0}/${p.data.sliceTotal}` : '') },
    { field: 'slotName', headerName: '슬롯', width: 110 },
    { headerName: '상태', width: 72, valueGetter: p => ordState(p.data),
      cellStyle: (p: any) => ({ color: p.value === '거부' ? colors.up : p.value === '완료' ? colors.title : colors.body, fontWeight: 600 }) },
    { headerName: '', width: 64, sortable: false, cellRenderer: (p: any) => (cancellable(p.data)
      ? <button style={{ fontSize: 11, padding: '0 8px', border: `1px solid ${colors.inputBorder}`, borderRadius: 3, background: colors.cardBg, cursor: 'pointer' }}
          onClick={e => { e.stopPropagation(); onCancel(p.data); }}>취소</button> : null) },
    { headerName: '메시지', flex: 1, minWidth: 200, valueGetter: p => p.data.errMsg ?? p.data.msg ?? '', tooltipValueGetter: p => p.value },
  ], [onCancel]);

  const sliceCols = useMemo<ColDef[]>(() => [
    { field: 't', headerName: '시각', width: 80, valueFormatter: p => String(p.value ?? '').slice(11, 19) },
    { field: 'ordId', headerName: '주문ID', width: 76 },
    { headerName: '종목', width: 150, valueGetter: p => nmOf.get(p.data.prodId) ?? p.data.prodId },
    { headerName: '구분', width: 66, cellRenderer: (p: any) => (p.data.ex != null ? sideBadge(p.data.ex > 0) : null) },
    numCol('qty2ord', '주문', 80),
    numCol('fillCumQty', '체결', 80),
    { headerName: '가격', width: 90, type: 'rightAligned',
      valueGetter: p => (p.data.limitPrc ? won(p.data.limitPrc) : p.data.limitIdx != null ? `${p.data.limitIdx}틱` : '시장가') },
    { field: 'ordNo', headerName: 'KIS주문번호', width: 110 },
    { field: 'ordAcceptT', headerName: '접수', width: 80, valueFormatter: p => String(p.value ?? '').slice(11, 19) },
    { headerName: '완료', width: 60, valueGetter: p => (p.data.done ? '✓' : '') },
    { headerName: '오류·메시지', flex: 1, minWidth: 200, valueGetter: p => p.data.errMsg ?? p.data.msg ?? '', tooltipValueGetter: p => p.value },
  ], [nmOf]);

  const fillCols = useMemo<ColDef[]>(() => [
    { field: 'ordT', headerName: '시각', width: 80, valueFormatter: p => { const s = String(p.value ?? ''); return s.length > 8 ? s.slice(11, 19) : s; } },
    { headerName: '종목', width: 160, valueGetter: p => p.data.prodNm ?? p.data.prodId, cellStyle: { fontWeight: 600 } },
    { headerName: '구분', width: 66, cellRenderer: (p: any) => sideBadge(p.data.ls === '02') },
    { field: 'ordNo', headerName: 'KIS주문번호', width: 110 },
    numCol('ordQty', '주문수량', 86),
    numCol('ordPrc', '주문가', 86),
    numCol('totFillQty', '체결수량', 86),
    numCol('avgPrc', '체결평균가', 96),
    numCol('totFillAmt', '체결금액', 110),
    numCol('rmnQty', '미체결', 76),
    numCol('rjctQty', '거부', 66),
    numCol('fee', '수수료', 76),
    numCol('tax', '세금', 76),
    { field: 'slotName', headerName: '슬롯', flex: 1, minWidth: 100 },
  ], []);

  const posCols = useMemo<ColDef[]>(() => [
    { headerName: '종목', width: 170, valueGetter: p => p.data.prodNm ?? p.data.prodId, cellStyle: { fontWeight: 600 } },
    numCol('pos', '보유', 80),
    numCol('ordpsblqty', '매도가능', 84),
    numCol('avgprc', '평단', 90),
    numCol('c', '현재가', 90),
    numCol('bamt', '매입금액', 110),
    numCol('evluamt', '평가금액', 110),
    numCol('prft', '평가손익(KIS)', 110, signCell),
    numCol('calcPrft', '평가손익(계산)', 110, { ...signCell, headerTooltip: '(현재가 − 평단) × 보유수량 — 비용 전' }),
    numCol('gap', '차이', 80, { cellStyle: (p: any) => ({ textAlign: 'right', color: Math.abs(p.value ?? 0) >= 1 ? colors.up : colors.hint }) }),
    { field: 'evlurt', headerName: '수익률', width: 80, type: 'rightAligned', valueFormatter: p => (p.value == null ? '' : pct(p.value)), ...signCell },
    numCol('prftkrw', '실현손익', 100, signCell),
  ], []);

  const pnlCols = useMemo<ColDef[]>(() => [
    { field: 'd', headerName: '일자', width: 96 },
    numCol('realized', '실현(비용 전)', 110, signCell),
    numCol('cost', '비용', 90),
    numCol('net', '실현(비용 후)', 110, signCell),
    numCol('unrealized', '평가손익', 110, signCell),
    numCol('total', '일손익', 110, { ...signCell, cellStyle: (p: any) => ({ textAlign: 'right', color: signColor(p.value), fontWeight: 700 }) }),
    numCol('kisDiffAmt', 'KIS 일손익', 110, signCell),
    numCol('kisGap', 'KIS 대비 차이', 110, { cellStyle: (p: any) => ({ textAlign: 'right', color: Math.abs(p.value ?? 0) >= 1 ? colors.up : colors.hint }) }),
    numCol('nFill', '체결수', 70),
    numCol('nSym', '종목수', 70),
    numCol('buyAmt', '매수대금', 120),
    numCol('sellAmt', '매도대금', 120),
  ], []);

  // 서버 pnl 은 '매도대금 − 매수대금' 이라 사기만 한 종목이 −100% 로 보인다.
  // 여기서는 사고 판 **겹치는 수량만** 손익으로 친다: min(매수,매도) × (매도평단 − 매수평단). 나머지는 보유로 남은 몫
  const tradeRows = useMemo(() => tradePnl.map(r => {
    const m = Math.min(r.bQty ?? 0, r.sQty ?? 0);
    return { ...r, mQty: m, mPnl: m ? m * ((r.sAvg ?? 0) - (r.bAvg ?? 0)) : null,
      mRt: m && r.bAvg ? ((r.sAvg ?? 0) / r.bAvg - 1) * 100 : null, rest: (r.bQty ?? 0) - (r.sQty ?? 0) };
  }), [tradePnl]);
  const tradeCols = useMemo<ColDef[]>(() => [
    { headerName: '종목', minWidth: 150, valueGetter: p => p.data.prodNm ?? p.data.prodId, cellStyle: { fontWeight: 600 } },
    numCol('bQty', '매수수량', 86), numCol('bAvg', '매수평단', 90), numCol('bAmt', '매수대금', 110),
    numCol('sQty', '매도수량', 86), numCol('sAvg', '매도평단', 90), numCol('sAmt', '매도대금', 110),
    numCol('mQty', '매칭수량', 86),
    numCol('mPnl', '매매손익(매칭분)', 120, signCell),
    { field: 'mRt', headerName: '손익률', width: 80, type: 'rightAligned', valueFormatter: p => (p.value == null ? '' : pct(p.value)), ...signCell },
    numCol('rest', '순매수 잔량', 96, { headerTooltip: '매수 − 매도 — 오늘 매매로 늘어난(+)/줄어든(−) 보유' }),
  ], []);

  const defaultColDef = useMemo<ColDef>(() => ({ resizable: true, sortable: true, cellDataType: false, cellStyle: { fontSize: '12px' } }), []);
  const fit = useMemo(() => ({ type: 'fitGridWidth' as const }), []);
  const h = (n: number) => agGridBox(Math.max(120, Math.min(44 + n * 30, 420)));

  const TABS: { id: Tab; label: string; n: number }[] = [
    { id: 'ord', label: '주문', n: orders.length },
    { id: 'slice', label: '슬라이스', n: selSlices.length },
    { id: 'fill', label: '체결', n: selFills.length },
    { id: 'pos', label: '보유종목', n: held.length },
    { id: 'pnl', label: '손익', n: pnl.length },
  ];
  const tabBtn = (on: boolean): React.CSSProperties => ({
    padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', borderRadius: 3,
    border: `1px solid ${on ? colors.btnPrimary : colors.inputBorder}`, background: on ? colors.btnPrimary : colors.cardBg, color: on ? colors.cardBg : colors.body,
  });
  const sum = (rows: any[], f: string) => rows.reduce((s, r) => s + (Number(r[f]) || 0), 0);

  return (
    <>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {TABS.map(t => <button key={t.id} style={tabBtn(tab === t.id)} onClick={() => setTab(t.id)}>{t.label} ({t.n})</button>)}
        {selOrdId != null && (
          <span style={{ fontSize: 12, color: colors.title, marginLeft: 4 }}>
            주문 #{selOrdId} 기준 <button style={{ ...tabBtn(false), padding: '1px 8px' }} onClick={() => setSelOrdId(null)}>전체 보기 ✕</button>
          </span>
        )}
      </div>

      {tab === 'ord' && (orders.length === 0 ? <div style={emptyMsg}>오늘 주문이 없습니다</div> : (
        <div className="ag-theme-quartz" style={h(orders.length)}>
          <AgGridReact rowData={[...orders].reverse()} columnDefs={ordCols} defaultColDef={defaultColDef} autoSizeStrategy={fit} getRowId={r => String(r.data.id)}
            tooltipShowDelay={300}
            onRowClicked={e => { if (e.data) { setSelOrdId(e.data.id); onPickProd(e.data.prodId); } }}
            getRowStyle={r => (r.data?.id === selOrdId ? { backgroundColor: colors.rowHover } : undefined)} />
        </div>
      ))}

      {tab === 'slice' && (selSlices.length === 0 ? <div style={emptyMsg}>슬라이스가 없습니다</div> : (
        <div className="ag-theme-quartz" style={h(selSlices.length)}>
          <AgGridReact rowData={selSlices} columnDefs={sliceCols} defaultColDef={defaultColDef} autoSizeStrategy={fit} getRowId={r => String(r.data.id)} tooltipShowDelay={300}
            onRowClicked={e => { if (e.data) setSelOrdId(e.data.ordId); }} />
        </div>
      ))}

      {tab === 'fill' && (selFills.length === 0 ? <div style={emptyMsg}>체결이 없습니다</div> : (
        <>
          <div style={{ fontSize: 12, color: colors.muted, marginBottom: 6 }}>
            매수 {won(sum(selFills.filter(f => f.ls === '02'), 'totFillAmt'))}원 · 매도 {won(sum(selFills.filter(f => f.ls === '01'), 'totFillAmt'))}원 · {selFills.length}건
          </div>
          <div className="ag-theme-quartz" style={h(selFills.length)}>
            <AgGridReact rowData={selFills} columnDefs={fillCols} defaultColDef={defaultColDef} autoSizeStrategy={fit} getRowId={r => String(r.data.id ?? `${r.data.ordNo}-${r.data.ordT}`)}
              onRowClicked={e => { if (e.data) onPickProd(e.data.prodId); }} />
          </div>
        </>
      ))}

      {tab === 'pos' && (held.length === 0 ? <div style={emptyMsg}>보유 종목이 없습니다 (계좌 {pfId ?? '-'})</div> : (
        <>
          <div style={{ fontSize: 12, color: colors.muted, marginBottom: 6 }}>
            평가 {won(sum(held, 'evluamt'))}원 · 평가손익 KIS <b style={{ color: signColor(sum(held, 'prft')) }}>{won(sum(held, 'prft'))}</b>
            {' '}/ 계산 <b style={{ color: signColor(sum(held, 'calcPrft')) }}>{won(sum(held, 'calcPrft'))}</b> — 계산 = (현재가−평단)×수량, 비용 전
          </div>
          <div className="ag-theme-quartz" style={h(held.length)}>
            <AgGridReact rowData={held} columnDefs={posCols} defaultColDef={defaultColDef} autoSizeStrategy={fit} getRowId={r => String(r.data.prodId)}
              onRowClicked={e => { if (e.data) onPickProd(e.data.prodId); }} />
          </div>
        </>
      ))}

      {tab === 'pnl' && (
        <>
          <div style={{ fontSize: 12, color: colors.muted, marginBottom: 6 }}>
            일손익 = 매매 현금흐름 + 평가금액 변화 − 비용 (순자산 변화와 같아야 한다). 실현은 평균단가 기준. KIS 일손익과의 차이가 1원 이상이면 빨강.
          </div>
          {pnl.length === 0 ? <div style={emptyMsg}>오늘 손익 집계가 아직 없습니다</div> : (
            <div className="ag-theme-quartz" style={h(pnl.length)}>
              <AgGridReact rowData={pnl} columnDefs={pnlCols} defaultColDef={defaultColDef} autoSizeStrategy={fit} getRowId={r => String(r.data.id ?? r.data.d)} />
            </div>
          )}
          <div style={{ fontSize: 12, fontWeight: 600, color: colors.title, margin: '14px 0 6px' }}>
            종목별 당일 매매손익 <span style={{ fontWeight: 400, color: colors.hint }}>(사고 판 겹치는 수량 기준, 비용 전)</span>
          </div>
          {tradePnl.length === 0 ? <div style={emptyMsg}>오늘 체결이 없습니다</div> : (
            <div className="ag-theme-quartz" style={h(tradePnl.length)}>
              <AgGridReact rowData={tradeRows} columnDefs={tradeCols} defaultColDef={defaultColDef} autoSizeStrategy={fit} getRowId={r => String(r.data.prodId)}
                onRowClicked={e => { if (e.data) onPickProd(e.data.prodId); }} />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default TradingAccountGrids;
